import { DEFAULT_LOCALE } from '../locales/index.js';
import { intlFormatter } from './intl.js';

/**
 * A US-dollar amount in the active locale's notation (ENG-380) — the one
 * place money is rendered. Tips are always charged in USD, so the currency
 * is fixed and only the presentation (symbol, its placement, the decimal
 * mark, the digits) follows the locale via `Intl`: a German user sees
 * "5,50 $", an Arabic one "‏5.50 US$", exactly as their other apps do.
 * Grouping stays off so the en-US output is byte-identical to the former
 * `$${amount}` / `$${amount.toFixed(2)}` templates it replaces.
 *
 * `cents` says how the two surfaces that show money treat the fraction:
 *   'auto'   (default) cents only when the amount has them — the streamer's
 *            cockpit, where "$5" is a glanceable tip and "$5.50" the exception
 *   'always' two decimals — the listener's ledger (history, dashboard), where
 *            amounts line up as a column of "$5.00"
 *   'never'  rounded to whole dollars — the watch queue's compact rail
 *
 *   formatUsd(5)                            → "$5"
 *   formatUsd(5.5)                          → "$5.50"
 *   formatUsd(5, 'en-US', { cents: 'always' })  → "$5.00"
 *   formatUsd(5.5, 'en-US', { cents: 'never' }) → "$6"
 *   formatUsd(5.5, 'de-DE')                 → "5,50 $"   (the space is U+00A0)
 */
export const formatUsd = (amount, locale = DEFAULT_LOCALE, { cents = 'auto' } = {}) => {
  const value = Number(amount) || 0;
  const digits = cents === 'never' || (cents === 'auto' && Number.isInteger(value)) ? 0 : 2;
  return intlFormatter(Intl.NumberFormat, locale, {
    style: 'currency',
    currency: 'USD',
    useGrouping: false,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
};
